import axiosInstant from "../../../api";
import { editProject } from "../../../api/endpoints";
import { apiErrorCode } from "../../../utils/apiError";
import { isInternal, resolveProjectBrandId } from "../../../utils";
import { BRAND_PROJECT_STATUSES } from "../helper";
import { BrandProject } from "./constants";

/**
 * The status transitions the approve/cancel dialogs drive. These replace the
 * internal-only Project Status dropdown in the edit modal for the two moves the
 * team makes by hand today: approving a new project, and cancelling one.
 *
 * The API already owns everything that hangs off these two statuses —
 * `Waiting For Brief` fires the "project approved" notification and fans the
 * move out to every sibling project on the same selection process, `Cancelled`
 * fires "project cancelled" and never fans out (see updateBrandProject in
 * brandProjects.js). So these helpers are a plain status PATCH; there is no
 * client-side bookkeeping to mirror.
 */
/** Resolves to the API's response body — the updated project. */
const patchProject = async (
    project: BrandProject,
    body: Record<string, unknown>
): Promise<Partial<BrandProject>> => {
    const brandId = resolveProjectBrandId(project);
    if (!brandId || !project?.id) {
        throw new Error("This project is missing a brand and cannot be updated.");
    }
    const res = await axiosInstant.patch(editProject(brandId, project.id), body);
    return (res && res.data) || {};
};

export const setProjectStatus = (project: BrandProject, status: string) =>
    patchProject(project, { status });

export const approveProject = (project: BrandProject) =>
    setProjectStatus(project, BRAND_PROJECT_STATUSES.BRIEF_PENDING);

/** A cancel must carry a reason — the API rejects one without. */
export const cancelProject = (project: BrandProject, reason: string) =>
    patchProject(project, {
        status: BRAND_PROJECT_STATUSES.CANCELLED,
        cancellation_reason: reason.trim()
    });

/**
 * Restore a cancelled project. A flag, not a status write: the SERVER decides
 * where it lands — alongside its live siblings when it shares a selection, or
 * back at pre_cancel_status when it is alone — and fires "project restored"
 * rather than replaying the landing status's own notification. The resolved
 * project carries the actual landing status.
 */
export const restoreProject = (project: BrandProject) =>
    patchProject(project, { uncancel: true });

// Mirrors PROCESS_STATUS_RANK in the API (brandProjects.js). Used only to
// PREVIEW where a restore will land; the server is the authority.
export const PROJECT_STATUS_RANK: Record<string, number> = {
    "Pending Approval": 0,
    "Waiting For Brief": 1,
    "Brief Review": 2,
    "Brief Denied": 2,
    "Expert Proposals": 3,
    "Experts Proposal Review": 4,
    "Experts Proposal Review Denied": 4,
    "Experts Selection": 5,
    Scheduling: 6,
    "Pre-Production": 7,
    Filming: 8,
    Editing: 9,
    "Video Review": 10,
    "Final Delivered": 11
};

// The same-rank "denied" variants the server's tie-break knows about.
const DENIED_STATUSES = new Set<string>([
    BRAND_PROJECT_STATUSES.BRIEF_DENIED,
    BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_DENIED
]);

export interface RestoreTarget {
    /** Where the restore will land, or null when it cannot be predicted. */
    status: string | null;
    /** True when the target comes from live siblings rather than pre_cancel_status. */
    rejoins: boolean;
}

/**
 * Where a restore lands, given the OTHER projects on the selection (from
 * useProcessSiblings). Same rule as the server: the most advanced live sibling
 * wins; a project alone on its process goes back to pre_cancel_status.
 */
export const restoreTargetFor = (
    project: BrandProject,
    siblings: Array<{ id: number; status?: string }>
): RestoreTarget => {
    const live = siblings.filter(
        (p) => Number(p.id) !== Number(project.id) && p.status && p.status !== BRAND_PROJECT_STATUSES.CANCELLED
    );
    // Most-advanced live sibling wins. Ties (Brief Review vs Brief Denied
    // share a rank): prefer the non-denied variant, exactly as the server
    // does — otherwise the preview can promise "Brief Denied" while the API
    // restores to "Brief Review".
    const best = live.reduce<string | null>((acc, p) => {
        const status = p.status as string;
        const rank = PROJECT_STATUS_RANK[status];
        if (rank === undefined) return acc;
        if (acc === null) return status;
        const accRank = PROJECT_STATUS_RANK[acc];
        const beatsOnTie = rank === accRank && DENIED_STATUSES.has(acc) && !DENIED_STATUSES.has(status);
        return rank > accRank || beatsOnTie ? status : acc;
    }, null);
    if (best !== null) return { status: best, rejoins: true };
    return { status: project.pre_cancel_status || null, rejoins: false };
};

/**
 * Only projects still sitting at Pending Approval are approvable — every later
 * status has already passed the gate, and re-approving would be a backward move.
 */
export const isAwaitingApproval = (project: BrandProject | null | undefined) =>
    project?.status === BRAND_PROJECT_STATUSES.PENDING;

export const canApproveProject = (
    project: BrandProject | null | undefined,
    role: string | null | undefined
) => isInternal(role) && isAwaitingApproval(project);

/**
 * Why cancelling is unavailable, or null when it is allowed.
 *
 * Mirrors the API's own rule rather than guessing: once a selection process
 * reaches Pre-Production its SOWs are committed and `updateBrandProject` rejects
 * ANY non-forward status move — including Cancelled, which carries no lifecycle
 * rank — with 423 SelectionLocked. `process_locked` is the same derived flag the
 * API checks, so gating on it here means the menu never offers a click that is
 * guaranteed to fail. Reverting the selection is the way back.
 */
export const cancelBlockedReason = (
    project: BrandProject | null | undefined
): string | null => {
    if (!project) return "This project is not available.";
    if (project.status === BRAND_PROJECT_STATUSES.CANCELLED) {
        return "This project is already cancelled.";
    }
    // process_locked is derived from brand_order_pros_id, so a project that never
    // joined a selection process has it false at EVERY status — including Final
    // Delivered, which the API would happily cancel. Delivered work is done; block
    // it on the status directly rather than relying on a lock that isn't there.
    if (project.status === BRAND_PROJECT_STATUSES.FINAL_DELIVERED) {
        return "This project has already been delivered and cannot be cancelled.";
    }
    if (project.process_locked) {
        return "This selection is committed — its SOWs are signed. Revert the selection before cancelling.";
    }
    return null;
};

export const canCancelProject = (
    project: BrandProject | null | undefined,
    role: string | null | undefined
) => isInternal(role) && cancelBlockedReason(project) === null;

/**
 * Why restoring is unavailable, or null when it is allowed. Mirrors the API:
 * only a Cancelled project with a recorded pre_cancel_status, and never onto a
 * committed selection (the restore would land below the lock).
 */
export const restoreBlockedReason = (
    project: BrandProject | null | undefined,
    opts: { hasLiveSiblings?: boolean } = {}
): string | null => {
    if (!project) return "This project is not available.";
    if (project.status !== BRAND_PROJECT_STATUSES.CANCELLED) {
        return "This project is not cancelled.";
    }
    if (project.process_locked) {
        return "This selection is committed — its SOWs are signed. Revert the selection before restoring.";
    }
    // pre_cancel_status only matters for a project ALONE on its selection —
    // one with live siblings rejoins them. Callers that don't know the sibling
    // state (the menus) leave hasLiveSiblings undefined and let the dialog,
    // which fetches it, and ultimately the API decide.
    if (opts.hasLiveSiblings === false && !project.pre_cancel_status) {
        return "This project was cancelled before its previous status was recorded, so it has to be set by hand.";
    }
    return null;
};

export const canRestoreProject = (
    project: BrandProject | null | undefined,
    role: string | null | undefined
) => isInternal(role) && restoreBlockedReason(project) === null;

/**
 * Why the brief dialog must not be opened for this project, or null when it may be.
 *
 * Submitting a brief writes `status = 'Brief Review'` server-side with no check on
 * the project's current status — and fans that out to sibling projects on the same
 * selection — so opening the editable brief on a Cancelled project is a silent
 * un-cancel. The status chip is not the only door in: the ⋯ menu item and the brief
 * status icon reach the same dialog, so all three ask this one function.
 *
 * Final Delivered was already blocked ad hoc at the chip; it lives here now so the
 * rule is stated once.
 */
export const briefBlockedReason = (
    project: BrandProject | null | undefined
): string | null => {
    if (!project) return null;
    if (project.status === BRAND_PROJECT_STATUSES.CANCELLED) {
        return "This project is cancelled. Restore it before working on the brief.";
    }
    if (project.status === BRAND_PROJECT_STATUSES.FINAL_DELIVERED) {
        return "This project has already been delivered.";
    }
    return null;
};

const PROJECT_STATUS_ERRORS: Record<string, string> = {
    SelectionLocked:
        "This selection is committed and can no longer be changed. Revert the selection first.",
    FilmWindowRequired: "Set the filming range for this selection first.",
    CancellationReasonRequired: "Please give a reason for cancelling.",
    ProjectNotCancelled: "This project is not cancelled.",
    PreCancelStatusMissing:
        "This project has no recorded previous status to restore to. Set its status by hand."
};

/** Turn an API failure into copy worth showing a user. */
export const projectStatusErrorMessage = (err: unknown): string => {
    const code = apiErrorCode(err);
    if (code && PROJECT_STATUS_ERRORS[code]) return PROJECT_STATUS_ERRORS[code];
    // An unmapped code is an API identifier ("BrandProjectNotUpdated"), not copy.
    // Keep it in the console for support and show the user something readable.
    if (code) {
        console.error("Unmapped project status error code:", code);
    }
    return "Couldn't update the project. Please try again.";
};
